/**
 * Single source of truth for the for-professionals page content.
 * Used by ProfessionalsDeckSpView.vue (runtime) and scripts/inject-route-meta.js
 * (build-time prerender for bots that don't run JS), so prices and features
 * can never drift between the page, the bot HTML and the JSON-LD.
 * CommonJS on purpose: the build script runs in plain Node.
 */
module.exports = {
  platformChips: [
    { t: "Agenda en tiempo real", s: "profesionales, salas y equipos a la vez" },
    { t: "Tipos de cita a medida", s: "duración, grupo, depósito, online" },
    { t: "Reservas online 24/7", s: "portal, app y Medkus.com" },
    { t: "App móvil", s: "tus pacientes en iOS y Android" },
    { t: "Videollamada integrada", s: "sin Zoom ni enlaces externos" },
    { t: "Historia clínica", s: "cifrada y con versionado" },
    { t: "Analíticas con IA", s: "lee el PDF y extrae los valores" },
    { t: "Programas terapéuticos", s: "diseño, presupuesto y venta online" },
    { t: "Rutinas para casa", s: "ejercicios, vídeos y seguimiento" },
    { t: "Portal del paciente", s: "Hoy, citas, pagos y documentos" },
    { t: "Firma digital", s: "consentimientos y presupuestos" },
    { t: "Comunicación", s: "WhatsApp, email y SMS" },
    { t: "Encuestas de seguimiento", s: "escalas, voz y estado de ánimo" },
    { t: "Cobros", s: "6 pasarelas, bonos, cuotas y SEPA" },
    { t: "Facturación", s: "Verifactu, caja y gastos con IA" },
    { t: "Informes", s: "11 informes y escritorio con widgets" },
    { t: "Formación", s: "vende tus cursos y certificaciones" },
    { t: "Membresías", s: "ingresos recurrentes" },
    { t: "Sesiones grupales", s: "aforos y mapa de plazas" },
    { t: "Marketing", s: "marketplace, campañas y cupones" },
    { t: "Multicentro", s: "varias sedes, una plataforma" },
    { t: "Seguridad", s: "AES-256, datos en la UE, 2FA" },
  ],

  featureSections: [
    {
      id: "crecer",
      q: "¿Cómo llenas tu agenda sin depender solo del boca a boca ni gastar en marketing?",
      a: "Medkus no solo gestiona tu consulta: trabaja para llenarla.",
      cards: [
        {
          h: "Captación y marketing",
          d: "Atrae y recupera pacientes, sin agencias.",
          pills: [
            "Perfil en el marketplace Medkus.com",
            "Reservas desde fuera de tu web",
            "Campañas de email y SMS",
            "Cupones y promociones",
            "Ocupar huecos: la IA te dice a quién llamar",
            "Lista de espera con aviso automático",
          ],
        },
        {
          h: "Presencia y reputación",
          d: "Conviértete en el referente de tu zona.",
          pills: [
            "Web propia con tu marca",
            "Blog y artículos",
            "Banners y destacados",
            "Testimonios y equipo",
            "Reseñas de pacientes",
          ],
        },
      ],
    },
    {
      id: "cuidar",
      q: "¿Cómo cuidas a un paciente dentro y fuera de la consulta?",
      a: "Con toda su historia y todo su seguimiento en un mismo lugar.",
      cards: [
        {
          h: "Historia clínica",
          d: "Su ficha completa, y su plan en segundos.",
          pills: [
            "Antecedentes, medicación y alergias",
            "Episodio por cita: motivo, exploración y diagnóstico",
            "Plantillas de diagnóstico y tratamiento",
            "Tratamiento con momentos del día",
            "Pruebas y analíticas estructuradas",
            "La IA lee el PDF de la analítica",
            "Evolución en línea de tiempo",
            "Informe médico en PDF con tu marca",
            "Versionado con motivo del cambio",
            "Consentimientos firmados",
          ],
        },
        {
          h: "Comunicación y seguimiento",
          d: "Para que nunca se sienta olvidado.",
          pills: [
            "WhatsApp Business en los dos sentidos",
            "Email y SMS",
            "Recordatorios y confirmaciones automáticas",
            "Mensajes del paciente desde el portal",
            "Encuestas de seguimiento con alertas",
            "Respuesta por voz: la IA transcribe y detecta el ánimo",
            "Rutinas entre visitas con vídeos",
            "Hoy: lo que le toca cada día",
          ],
        },
      ],
    },
    {
      id: "tiempo",
      q: "¿Por qué desaparecen tantas horas cada semana?",
      a: "Porque la agenda, el cobro y el papeleo los hace una persona. Aquí se hacen solos.",
      cards: [
        {
          h: "Agenda inteligente",
          d: "Di adiós a los WhatsApps de confirmación.",
          pills: [
            "Profesionales, salas y equipos a la vez",
            "Tipos de cita con duración, grupo y depósito",
            "Citas online con videollamada",
            "Series de citas repetidas",
            "Mapa de plazas para grupos",
            "Lista de espera y relleno de huecos",
            "Recordatorios por email, SMS y WhatsApp",
            "Confirmación automática",
            "Consentimientos enviados solos antes de la cita",
            "Portal, app y Medkus.com",
          ],
        },
        {
          h: "Facturación automática",
          d: "Conforme a la normativa fiscal española.",
          pills: [
            "Verifactu con cuadre mensual",
            "TPV, caja y arqueo",
            "Stripe, Redsys, PayPal, Authorize.Net, MercadoPago y NetPay",
            "Cuotas y domiciliación SEPA",
            "Depósitos y enlaces de pago",
            "Pago a plazos",
            "Gastos con foto del ticket por IA",
            "Stock y proveedores",
            "Exportación a Excel",
          ],
        },
        {
          h: "Programas y presupuestos",
          d: "Monta el programa y envíalo en un par de clics.",
          pills: [
            "Programa por días o por fases",
            "Precio cerrado, suma de sus piezas",
            "Presupuesto en PDF firmado",
            "Pago único o a plazos",
            "Citas del programa ya pagadas",
            "Seguimiento del avance",
          ],
        },
      ],
    },
    {
      id: "ingresos",
      q: "¿Cómo ganas más sin trabajar más horas?",
      a: "Convirtiendo tu conocimiento en productos que se venden solos.",
      cards: [
        {
          h: "Membresías",
          d: "Ingresos recurrentes.",
          pills: [
            "Cuotas semanales a anuales",
            "Cobro recurrente con tarjeta o SEPA",
            "Bonos de dinero, sesiones u horas",
          ],
        },
        {
          h: "Programas terapéuticos",
          d: "Diseña tu programa y véndelo online.",
          pills: [
            "Diseñador visual",
            "Generados con IA",
            "Venta online desde el portal",
            "Galería con vídeo",
            "Seguimiento y avisos",
          ],
        },
        {
          h: "Sesiones grupales y clases",
          d: "Talleres, retiros y grupos.",
          pills: [
            "Inscripción online",
            "Aforos y mapa de plazas",
            "Actividades y eventos",
            "Bonos y packs",
          ],
        },
      ],
    },
    {
      id: "todo",
      q: "¿Qué más hay dentro?",
      a: "Lo que no cabe en una demo de veinte minutos.",
      cards: [
        {
          h: "Portal y app del paciente",
          d: "Su centro, en el bolsillo.",
          pills: [
            "Reservar, confirmar y cancelar",
            "Pagar y ver deudas",
            "Tienda y pedidos",
            "Programas y rutinas",
            "Tratamiento vigente",
            "Formaciones",
            "Encuestas",
            "Firmar documentos",
            "Informes recibidos",
            "Cinco idiomas",
          ],
        },
        {
          h: "Equipo y configuración",
          d: "Cada centro, a su manera.",
          pills: [
            "Roles con permisos por pantalla",
            "Usuarios con perfil profesional",
            "Idioma, zona horaria y moneda por centro",
            "Campos personalizados cifrables",
            "Bibliotecas de documentos y plantillas",
            "Galería multimedia con YouTube y Vimeo",
            "Asistente de ayuda con IA",
            "Importación de datos",
          ],
        },
        {
          h: "Informes y datos",
          d: "Decisiones con números, no con sensaciones.",
          pills: [
            "Facturación por fechas",
            "Consumos por producto, vendedor y recurso",
            "Cancelaciones",
            "Valor del cliente",
            "Saldos pendientes",
            "Ocupación de recursos",
            "Bonos",
            "Relleno de huecos",
            "Rentabilidad",
            "Escritorio con widgets",
          ],
        },
      ],
    },
    {
      id: "seguridad",
      q: "¿Y los datos de tus pacientes?",
      a: "Tratados como lo que son: datos de salud.",
      cards: [
        {
          h: "Protección de datos",
          d: "RGPD sanitario de verdad, no una casilla.",
          pills: [
            "RGPD y LOPDGDD",
            "Datos clínicos cifrados con AES-256-GCM",
            "Adjuntos cifrados",
            "Base de datos propia por centro",
            "Verificación en dos pasos",
            "Permisos por rol y pantalla",
            "Registro de accesos",
            "Versionado clínico",
            "Contrato de encargado de tratamiento",
            "ISO 27001 en proceso",
          ],
        },
        {
          h: "Infraestructura",
          d: "Tus datos, en Europa y por duplicado.",
          pills: [
            "Servidores dedicados OVH en Francia (UE)",
            "Réplica en tiempo real",
            "Copias diarias cifradas, 30 días",
            "Copia fuera del proveedor",
            "Exportación completa",
            "Sin permanencia",
          ],
        },
      ],
    },
  ],

  formacionPills: [
    "Másters para el público",
    "Certificaciones",
    "Venta e inscripción online",
    "Pago único o por fases",
    "También para tu equipo",
  ],

  nicheTags: [
    "Medicina integrativa",
    "Medicina funcional",
    "Antiaging",
    "Longevity",
    "Recovery",
    "Biohacking",
    "PNI y epigenética",
    "Salud hormonal",
    "Salud de la mujer",
    "Nutrición",
    "Bienestar",
  ],

  featureList: [
    "Agenda multicentro en tiempo real con profesionales, salas y equipos como recursos",
    "Tipos de cita configurables: duración, grupo, mapa de plazas, depósito, antelaciones y límites",
    "Citas online con videollamada integrada propia",
    "Series de citas repetidas y cancelación múltiple",
    "Lista de espera con aviso automático y relleno de huecos con sugerencias de IA",
    "Recordatorios y confirmaciones automáticas por email, SMS y WhatsApp",
    "Envío automático de consentimientos a firmar antes de la cita",
    "Reservas online 24/7 en el portal del paciente, la app móvil y el marketplace Medkus.com",
    "Ficha única del paciente con formularios configurables y campos personalizados cifrables",
    "Historia clínica: antecedentes, medicación, alergias y episodio por cita con diagnóstico y tratamiento",
    "Plantillas clínicas de diagnóstico, tratamiento, pruebas y textos libres",
    "Pruebas y analíticas estructuradas; la IA lee el PDF y extrae los valores",
    "Evolución clínica en línea de tiempo e informe médico en PDF con la marca del centro",
    "Versionado clínico con autor, fecha y motivo del cambio",
    "Consentimientos y documentos con firma digital y versionado",
    "Programas terapéuticos por días o fases con presupuesto firmado, pago único o a plazos y venta online",
    "Rutinas para casa con vídeos, entregas del paciente y revisión profesional",
    "Pantalla Hoy en el portal del paciente con lo que le toca cada día",
    "Formaciones online con módulos, lecciones, correcciones y venta",
    "WhatsApp Business bidireccional, email y SMS con plantillas y campañas",
    "Encuestas de seguimiento con escalas, alertas y respuesta por voz: la IA transcribe el audio y detecta el estado de ánimo",
    "TPV, caja y arqueo, stock y gastos con foto del ticket leída por IA",
    "Pasarelas Stripe, Redsys, PayPal, Authorize.Net, MercadoPago y NetPay",
    "Bonos de dinero, sesiones u horas; cuotas recurrentes con tarjeta o SEPA; cupones",
    "Depósitos, enlaces de pago y pago a plazos",
    "Facturación conforme a Verifactu con cuadre mensual contra Hacienda",
    "Once informes: facturación, consumos, cancelaciones, valor del cliente, deudas, ocupación, bonos, huecos y rentabilidad",
    "Portal del paciente con marca propia: reservas, pagos, tienda, programas, rutinas, documentos e informes",
    "App móvil para pacientes en iOS y Android",
    "Roles con permisos por pantalla y acción, verificación en dos pasos y registro de accesos",
    "Datos clínicos y adjuntos cifrados con AES-256-GCM; base de datos aislada por centro",
    "Servidores dedicados OVH en Francia (UE) con réplica en tiempo real y copias diarias cifradas de 30 días",
    "RGPD y LOPDGDD con contrato de encargado de tratamiento; ISO 27001 en proceso",
    "Exportación completa de datos sin permanencia",
    "Cinco idiomas (español de España y México, inglés, francés e italiano) y cuatro monedas",
  ],

  pricingHeadline: "Precios cerrados, sin permanencia. Y el cambio lo hacemos nosotros.",
  tiers: [
    {
      lab: "En solitario",
      price: "50 €",
      amount: 50,
      unit: "/ mes",
      who: "Profesionales del bienestar no sanitarios que trabajan por su cuenta. Incluye 10 € de IA al mes.",
    },
    {
      lab: "Consulta sanitaria",
      price: "80 €",
      amount: 80,
      unit: "/ mes",
      who: "Médicos y sanitarios en solitario, con historia clínica completa y Verifactu. Incluye 10 € de IA al mes.",
    },
    {
      lab: "Clínica 1-5",
      price: "150 €",
      amount: 150,
      unit: "/ mes",
      who: "Hasta 5 profesionales con agenda. Incluye 20 € de IA al mes.",
    },
    {
      lab: "Clínica 6-10",
      price: "250 €",
      amount: 250,
      unit: "/ mes",
      who: "Hasta 10 profesionales con agenda. Incluye 30 € de IA al mes.",
    },
    {
      lab: "Clínica 11-15",
      price: "350 €",
      amount: 350,
      unit: "/ mes",
      who: "Hasta 15 profesionales con agenda. Incluye 40 € de IA al mes.",
    },
    {
      lab: "Multicentro",
      price: "a convenir",
      amount: null,
      unit: "",
      who: "Más de 15 profesionales o varias sedes: precio por sede con descuento.",
    },
  ],
  pricingNote:
    "Cuentan solo los profesionales con agenda; recepción y administración no suman. La IA incluida es un crédito mensual: si un mes se supera, el exceso se factura a la tarifa publicada, sin sorpresas.",
};
